import { useState } from 'react';
import { getConsentState, setConsentState } from '../utils/consent';

/**
 * Lightweight in-app analytics consent banner. Pinned to the top of
 * the viewport like a native iOS notification banner — not a floating
 * popup, since App Review tends to flag web-style cookie popups.
 *
 * One-time on first launch; decision persists. Decline by default
 * (privacy-first); explicit Allow is required to load GA4.
 */
export default function CookieBanner() {
  // Visible only on first session — once decided, getConsentState
  // returns 'granted' or 'denied' and the banner stays hidden.
  const [visible, setVisible] = useState(() => getConsentState() === null);
  if (!visible) return null;

  function decide(value) {
    setConsentState(value);
    setVisible(false);
  }

  return (
    <div style={wrap} role="dialog" aria-label="Analytics preference">
      <div style={card}>
        <div style={iconWrap} aria-hidden>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12h18M12 3v18" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={title}>Help improve Proper Miles?</div>
          <div style={body}>
            Share anonymous usage data to help us spot issues and improve the app.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={() => decide('granted')} style={primaryBtn}>Allow</button>
            <button onClick={() => decide('denied')} style={ghostBtn}>Not now</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const wrap = {
  position: 'fixed', left: 0, right: 0, top: 0,
  zIndex: 9000,
  // Top safe-area inset so the banner doesn't jam under the iPhone notch
  paddingTop: 'env(safe-area-inset-top, 0px)',
  paddingLeft: 12, paddingRight: 12,
  pointerEvents: 'none',
};

const card = {
  pointerEvents: 'auto',
  margin: '8px auto 0',
  maxWidth: 540,
  display: 'flex', alignItems: 'flex-start', gap: 12,
  background: 'var(--bg-card, #2a2a2a)',
  color: 'var(--text-primary, #fff)',
  border: '1px solid var(--border, rgba(255,255,255,0.1))',
  borderRadius: 14,
  padding: '12px 14px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
};

const iconWrap = {
  width: 32, height: 32, borderRadius: 8,
  background: 'rgba(211,71,74,0.15)',
  color: '#D3474A',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

const title = {
  fontSize: '0.875rem', fontWeight: 700,
  color: 'var(--text-primary, #fff)',
  marginBottom: 2,
};

const body = {
  fontSize: '0.8125rem',
  color: 'var(--text-secondary, rgba(255,255,255,0.75))',
  lineHeight: 1.4,
};

const primaryBtn = {
  padding: '7px 14px', fontSize: '0.8125rem', fontWeight: 600,
  background: 'var(--accent, #D3474A)', color: '#fff',
  border: 'none', borderRadius: 8, cursor: 'pointer',
};

const ghostBtn = {
  padding: '7px 14px', fontSize: '0.8125rem', fontWeight: 600,
  background: 'transparent',
  color: 'var(--text-secondary, rgba(255,255,255,0.85))',
  border: '1px solid var(--border, rgba(255,255,255,0.2))',
  borderRadius: 8, cursor: 'pointer',
};
